import React, { useState, useMemo } from 'react';
import { Table, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './AtozResources.css';
import { FaEdit, FaTrash, FaPlus, FaEye, FaFilePdf, FaVideo, FaExternalLinkAlt } from 'react-icons/fa';
import AddAtozResourceModal from './AddAtozResourceModal';
import { 
  useGetAtozResourcesQuery, 
  useAddAtozResourceMutation, 
  useUpdateAtozResourceMutation, 
  useDeleteAtozResourceMutation 
} from "../Services/SecondaryApi";
import Swal from 'sweetalert2';
import SearchImg from "../Assets/Images/search-img.svg";
import FilterImg from "../Assets/Images/Filters lines.svg";

const AtozResources = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { 
    data: resources = [], 
    isLoading, 
    error,
    refetch 
  } = useGetAtozResourcesQuery();

  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'ascending'
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page

  // Sort resources
  const sortedResources = useMemo(() => {
    if (!resources?.data) return [];
    
    const sortableItems = [...resources.data];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [resources, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const [addResource] = useAddAtozResourceMutation();
  const [updateResource] = useUpdateAtozResourceMutation();
  const [deleteResource] = useDeleteAtozResourceMutation();

  const handleAddClick = () => {
    setEditingResource(null);
    setShowModal(true);
  };

  const handleEditClick = (resource) => {
    setEditingResource(resource);
    setShowModal(true);
  };

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this resource!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        await deleteResource(id).unwrap();
        await refetch();
        Swal.fire('Deleted!', 'The resource has been deleted.', 'success');
      } catch (error) {
        console.error('Delete error:', error);
        Swal.fire('Error!', 'Failed to delete the resource.', 'error');
      }
    }
  };

  const handleSaveResource = async (formData) => {
    try {
      let response;
      
      if (editingResource) {
        // For update, we need to send the form data directly
        response = await updateResource({ 
          id: editingResource.id, 
          data: formData 
        }).unwrap();
      } else {
        // For create, we can send the form data directly
        response = await addResource(formData).unwrap();
      }
      
      // Check if the response indicates success
      if (response.success || response.status === 'success') {
        await refetch();
        setShowModal(false);
        Swal.fire({
          title: 'Success!',
          text: response.message || (editingResource ? 'Resource updated successfully!' : 'Resource added successfully!'),
          icon: 'success',
          confirmButtonColor: '#28a745'
        });
      } else {
        // Handle API response that's not successful but didn't throw an error
        throw new Error(response.message || 'Operation failed');
      }
      
    } catch (error) {
      console.error('Save error:', error);
      
      // Extract error message from different possible response formats
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.data) {
        // Handle different error response formats
        if (typeof error.data === 'string') {
          errorMessage = error.data;
        } else if (error.data.message) {
          errorMessage = error.data.message;
        } else if (error.data.errors) {
          // Handle Laravel validation errors
          const errorMessages = [];
          Object.entries(error.data.errors).forEach(([field, messages]) => {
            errorMessages.push(...(Array.isArray(messages) ? messages : [messages]));
          });
          errorMessage = errorMessages.join('\n');
        }
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error to user
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
        customClass: {
          confirmButton: 'swal2-confirm'
        }
      });
    }
  };

  // Extract resources from the nested data structure
  const resourcesList = resources?.data || [];
  
  const filteredResources = sortedResources.filter(resource =>
    resource.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.tooltip?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredResources.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Helper function to display pagination numbers with dots
  const getPaginationNumbers = () => {
    const pageNumbers = [];
    
    if (totalPages <= 6) {
      // Show all page numbers if 6 or fewer pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show first page, last page, and pages around current page
      pageNumbers.push(1);
      
      if (currentPage > 3) {
        pageNumbers.push('...');
      }
      
      // Show current page and its neighbors
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pageNumbers.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pageNumbers.push('...');
      }
      
      if (currentPage < totalPages - 1) {
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-4">
        <Alert variant="danger">
          Error loading resources: {error.message || 'Failed to load resources. Please try again.'}
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <Alert variant="danger">
          Error loading resources: {error.message || 'Failed to load resources. Please try again later.'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <h3>A to Z Resources</h3>
      
      <div className="table-wrapper-div">
        <div className="sub-haed-table">
          <div className="d-flex justify-content-space-between">
            <div className="search-bar">
              <img src={SearchImg} alt="Search" className="SearchIcon"/>
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="add-div">
              <button className="primary-btn" onClick={handleAddClick}>
                <FaPlus className="me-1" /> Add Resource
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <Table className="table-common">
            <thead>
              <tr>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => requestSort('id')}
                >
                  #
                  {sortConfig.key === 'id' && (
                    <span className="ms-1">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  style={{ cursor: 'pointer' }}
                  onClick={() => requestSort('name')}
                >
                  Resource Name
                  {sortConfig.key === 'name' && (
                    <span className="ms-1">
                      {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th className="text-center">Tooltip</th>
                <th className="text-center">Tags</th>
                <th className="text-center">PDF</th>
                <th className="text-center">Video</th>
                <th className="text-center">Editable Link</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((resource, index) => (
                  <tr key={resource.id}>
                    <td>{resource.id}</td>
                    <td>{resource.name}</td>
                    <td className="text-center">
                      {resource.tooltip ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip 
                              id={`tooltip-${resource.id}`}
                              className="custom-tooltip"
                              style={{
                                '--tooltip-bg': 'white',
                                '--tooltip-color': 'black',
                                '--tooltip-max-width': '400px',
                                '--tooltip-padding': '10px',
                                '--tooltip-border-radius': '4px',
                                '--tooltip-box-shadow': '0 2px 10px rgba(0,0,0,0.15)'
                              }}
                            >
                              <div style={{ textAlign: 'left' }}>{resource.tooltip}</div>
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'pointer' }}>
                            <FaEye className="text-info" />
                          </span>
                        </OverlayTrigger>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      {resource.tags?.length > 0 ? (
                        <div className="d-flex flex-wrap justify-content-center gap-1">
                          {resource.tags.map(tag => (
                            <span 
                              key={tag.id} 
                              className="badge bg-secondary text-white px-2 py-1 resource-tag-list"
                              style={{
                                fontSize: '0.8rem',
                                fontWeight: 'normal',
                                borderRadius: '4px',
                                backgroundColor: '#ffc107',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '120px',
                                display: 'inline-block'
                              }}
                              title={tag.name}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      {resource.presigned_pdf_url ? (
                        <a 
                          href={resource.presigned_pdf_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-icon text-danger"
                          title="View PDF"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '24px',
                            height: '24px',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease',
                            fontSize: '16px'
                          }}
                        >
                          <FaFilePdf />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      {resource.video_url ? (
                        <a 
                          href={resource.video_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-icon"
                          title="View Video"
                        >
                          <FaVideo className="text-primary" />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      {resource.editable_link ? (
                        <a 
                          href={resource.editable_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-icon text-primary"
                          title={resource.editable_link}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '24px',
                            height: '24px',
                            textDecoration: 'none',
                            transition: 'color 0.2s ease',
                            fontSize: '16px'
                          }}
                        >
                          <FaExternalLinkAlt />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center">
                        <button 
                          className="btn-outline me-2"
                          onClick={() => handleEditClick(resource)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-outline"
                          onClick={() => handleDeleteClick(resource.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    {searchTerm ? 'No matching resources found' : 'No resources available'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {filteredResources.length > itemsPerPage && (
        <div className="pagination-wrapper mt-3">
          <nav>
            <ul className="pagination justify-content-center mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              
              {getPaginationNumbers().map((number, index) => (
                <li 
                  key={index} 
                  className={`page-item ${number === currentPage ? 'active' : ''} ${number === '...' ? 'disabled' : ''}`}
                >
                  <button 
                    className="page-link" 
                    onClick={() => typeof number === 'number' ? paginate(number) : null}
                  >
                    {number}
                  </button>
                </li>
              ))}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          <div className="text-center text-muted mt-2">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredResources.length)} of {filteredResources.length} entries
          </div>
        </div>
      )}

      <AddAtozResourceModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSaveResource={handleSaveResource}
        resource={editingResource}
      />
    </div>
  );
};

export default AtozResources;
